// Bundles this job's dependencies (REST client, call engine, media control,
// echo test, live WS-event mirror) the same way `shell/accountContext.tsx`
// bundles the account service — screens read them with `useScreens()` instead
// of prop-drilling through the router. The orchestrator constructs the real
// instances once (see this job's final report for the exact App.tsx wiring)
// and wraps the routed tree in one `<ScreensProvider>`.

import { createContext, useContext, type ReactNode } from 'react';
import type { PendingInvitePickup } from '../push/pendingInvite.js';
import type { RestClient } from '@merkury/brain/api/client.js';
import type { LiveEvents } from '@merkury/brain/api/liveEvents.js';
import type { CallEngineLike, EchoTestLike, MediaControlLike } from './call/engineTypes.js';

export interface ScreensDeps {
  api: RestClient;
  engine: CallEngineLike;
  media: MediaControlLike;
  echoTest: EchoTestLike;
  liveEvents: LiveEvents;
  /** Cold-open/notification pending-invite source (absent pre-session/tests). */
  pickup?: PendingInvitePickup;
  /**
   * Re-sync the Web Push subscription (idempotent). Screens call it after the
   * user grants notification permission so a subscription is created without a
   * reload. Absent pre-session/tests (and a no-op when push is unavailable).
   */
  syncPush?: () => Promise<void>;
}

const ScreensDepsContext = createContext<ScreensDeps | undefined>(undefined);

export function ScreensProvider({
  deps,
  children,
}: {
  deps: ScreensDeps;
  children: ReactNode;
}): ReactNode {
  return <ScreensDepsContext.Provider value={deps}>{children}</ScreensDepsContext.Provider>;
}

export function useScreens(): ScreensDeps {
  const value = useContext(ScreensDepsContext);
  if (!value) throw new Error('useScreens must be used inside <ScreensProvider>');
  return value;
}
